import React, { useMemo, useRef, useState, useCallback } from "react";
import NexusGrid from "../../core/NexusGrid";

const genId = () =>
  `dep_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;

const Departments = ({ questionnaireData, setQuestionnaireData }) => {
  const gridRef = useRef(null);
  const [rows, setRows] = useState(() => {
    const saved = questionnaireData?.foundationalSetup?.departmentsHierarchy;
    return Array.isArray(saved) && saved.length > 0
      ? saved.map((r, idx) => ({
          id: r.id || `dep_${idx}_${Date.now()}`,
          ...r,
        }))
      : [];
  });

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Department ID",
        field: "departmentId",
        editable: true,
        width: 220,
      },
      {
        headerName: "Department Name",
        field: "departmentName",
        editable: true,
        flex: 1,
      },
    ],
    []
  );

  const getRowStyle = useCallback((p) => {
    const s = p.data?.__status;
    if (s === "new") return { backgroundColor: "#f0fdf4" };
    if (s === "deleted") return { backgroundColor: "#fef2f2" };
    if (s === "merged") return { backgroundColor: "#fffbeb" };
    return undefined;
  }, []);

  const onCellValueChanged = useCallback(
    (params) => {
      const id = params.data?.id;
      if (!id) return;
      setRows((prev) =>
        prev.map((r) =>
          r.id === id ? { ...r, [params.colDef.field]: params.newValue } : r
        )
      );
      setQuestionnaireData((prev) => ({
        ...prev,
        foundationalSetup: {
          ...prev.foundationalSetup,
          departmentsHierarchy: (
            prev?.foundationalSetup?.departmentsHierarchy || []
          ).map((r) =>
            r.id === id ? { ...r, [params.colDef.field]: params.newValue } : r
          ),
        },
      }));
    },
    [setQuestionnaireData]
  );

  const handleAdd = () => {
    const newRow = {
      id: genId(),
      departmentId: "",
      departmentName: "",
      __status: "new",
    };
    setRows((prev) => [newRow, ...prev]);
    setQuestionnaireData((prev) => ({
      ...prev,
      foundationalSetup: {
        ...prev.foundationalSetup,
        departmentsHierarchy: [
          newRow,
          ...(prev?.foundationalSetup?.departmentsHierarchy || []),
        ],
      },
    }));
    const api = gridRef.current?.api;
    if (api && api.paginationGoToFirstPage) api.paginationGoToFirstPage();
  };

  const refreshRowById = useCallback((id) => {
    const api = gridRef.current?.api;
    if (!api) return;
    const node = id ? api.getRowNode(id) : null;
    if (node)
      api.refreshCells({ rowNodes: [node], force: true, columns: ["actions"] });
    api.refreshCells({ force: true, columns: ["rowNumber"] });
  }, []);

  const setDataForContext = useCallback(
    (updater) => {
      setRows((prev) => {
        const next = updater(prev);
        setQuestionnaireData((prevQ) => ({
          ...prevQ,
          foundationalSetup: {
            ...prevQ.foundationalSetup,
            departmentsHierarchy: next,
          },
        }));
        return next;
      });
    },
    [setQuestionnaireData]
  );

  return (
    <NexusGrid
      title="Departments"
      actionText="Add Department"
      onAdd={handleAdd}
      columnDefs={columnDefs}
      rowData={rows}
      showRowNumbers={true}
      showActions={true}
      onCellValueChanged={onCellValueChanged}
      getRowId={(p) => p.data.id}
      pagination={true}
      paginationPageSize={10}
      immutableData={true}
      containerClassName="border border-gray-200 rounded-lg bg-white shadow-sm mb-6"
      gridRef={gridRef}
      rowHeight={36}
      getRowStyle={getRowStyle}
      context={{ setRowData: setDataForContext, refreshRowById }}
    />
  );
};

export default Departments;
